import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Input,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  getSettingsRequest,
  saveOfferRequest,
  expireOfferRequest,
} from "../../../redux/settings/settingsSlice";
import { canShowPermission } from "../Permissions/permissionObject";

const Advertisement = () => {
  const dispatch = useDispatch();
  const { data } = useSelector((state) => state.settingData);
  const offers = data?.data?.offers || [];

  const [offerFile, setOfferFile] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isSavingOffer, setIsSavingOffer] = useState(false);

  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canCreate = canShowPermission(permissions, "advertisement", "create");
  const canEdit = canShowPermission(permissions, "advertisement", "edit");
  const canDelete = canShowPermission(permissions, "advertisement", "delete");

  useEffect(() => {
    dispatch(getSettingsRequest());
  }, [dispatch]);

  function formatDateTimeLocal(dateString) {
    if (!dateString) return "N/A";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }

  const handleSaveOffer = async () => {
    if (isSavingOffer) return; // prevent double clicks

    //  Validation
    if (!offerFile) {
      toast.error("Offer banner file is required");
      return;
    }
    if (!startDate) {
      toast.error("Start date is required");
      return;
    }
    if (!endDate) {
      toast.error("End date is required");
      return;
    }

    setIsSavingOffer(true);

    try {
      await dispatch(
        saveOfferRequest({
          file: offerFile,
          startDate,
          endDate,
        })
      );
      toast.success("Offer saved successfully!");
      setOfferFile(null);
      setStartDate("");
      setEndDate("");
      dispatch(getSettingsRequest());
    } catch (err) {
      console.error("Failed to save offer:", err);
      toast.error("Failed to save offer.");
    } finally {
      //  small delay for smooth UX
      setTimeout(() => {
        setIsSavingOffer(false);
      }, 500);
    }
  };

  const handleRemoveOffer = (id) => {
    dispatch(expireOfferRequest({ id: id }));
    dispatch(getSettingsRequest());
  };

  return (
    <Box sx={{ width: "100%", mt: 0, mb: { xs: 2, md: 2 } }}>
      <Paper
        elevation={2}
        sx={{ p: 3, width: "100%", border: "1px solid #ccc" }}
      >
        <Typography variant="h6" fontWeight={700} mb={2}>
          Banner Integration
        </Typography>

        {/* 1. Upload Banner - First Row */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography fontWeight={500} mb={1}>
              Offer Banner (file size should be less than 300 kb)
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {(canCreate || canEdit) && (
              <label htmlFor="offer-file-upload">
                <Input
                  id="offer-file-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setOfferFile(e.target.files[0]);
                    }
                  }}
                  sx={{ display: "none" }}
                />
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  sx={{
                    textTransform: "none",
                    borderColor: "#7b8499",
                    color: "#7b8499",
                    "&:hover": {
                      borderColor: "#752ADA",
                      color: "#752ADA",
                    },
                  }}
                >
                  Upload Offer Banner
                </Button>
              </label>
              )}
              {offerFile && (
                <Typography
                  variant="body2"
                  fontSize={14}
                  sx={{ color: "#555", fontStyle: "italic" }}
                >
                  {offerFile.name}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* 2. Start and End Date - Second Row */}
        {(canCreate || canEdit) && (
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Offer Date"
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                // Reset end date if it's before the new start date
                if (endDate && e.target.value > endDate) {
                  setEndDate("");
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: new Date().toISOString().split("T")[0], // ✅ only allow today & future
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Offer Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDate || undefined, // Restricts selection before start date
              }}
            />
          </Grid>
        </Grid>
        )}

        {/* 3. Active Advertisement - Third Row */}
        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={6}>
            <Typography fontWeight={500}>Active Advertisement</Typography>
            <Box
              border="1px solid #ccc"
              borderRadius={1}
              p={2}
              minHeight="220px"
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={2}
            >
              {offers && offers.length > 0 ? (
                <>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Active Banner Image
                  </Typography>
                  <img
                    src={offers[0].bannerLink}
                    alt="Active Offer"
                    height={220}
                    width={220}
                    style={{ objectFit: "contain", borderRadius: 8 }}
                  />
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    color="text.secondary"
                  >
                    Offer Between {formatDateTimeLocal(offers[0].startDate)} to{" "}
                    {formatDateTimeLocal(offers[0].endDate)}
                  </Typography>
                  {canDelete && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleRemoveOffer(offers[0]._id)}
                    >
                      Remove
                    </Button>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No active advertisement
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* 4. Submit Button */}
        {(canCreate || canEdit) && (
          <Button
            variant="contained"
            sx={{
              bgcolor: "#752ADA",
              px: 6,
              textTransform: "none",
              mt: 3,
            }}
            onClick={handleSaveOffer}
            disabled={isSavingOffer}
          >
            {isSavingOffer ? "Saving..." : "Add Offer"}
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default Advertisement;

